import { EventTypes, useEditor } from '@candulabs/craft';

import { CustomNodeData, HANDLER_TYPES } from 'craft/types';

import { useFlow } from './useFlow';
import { useFlowGraph } from './useFlowGraph';

/**
 * We have delete as a separate hook because it uses both
 * useFLow and useFlowGraph.
 * Because useFlowGraph calls useFlow and useFlowDelet needs both,
 * we can't write this logic inside useFlow or else we'll get infinite recursion
 * @param nodeId
 */
export const useFlowDelete = (nodeId: string) => {
  const { actions, query } = useEditor();
  const flow = useFlow(nodeId);
  const flowGraph = useFlowGraph(nodeId);

  const onDeleteStepId = (stepId: string) => {
    const index = flow.getStepIndex(stepId);
    const deletingActiveStep = flow.activeStepId === stepId;

    // only used if deleting the active step
    // if we're deleting the first step select the new first one, otherwise select previous step
    const stepIdToSelect =
      index === 0 ? flow.getNextStepId(stepId) : flow.getPreviousStepId(stepId);

    actions.produceState((_, noHistoryActions) => {
      noHistoryActions.deleteNodes(query.node(nodeId).get().data.nodes[index]);

      flowGraph.edges
        .filter(
          (edge) => edge.toStepId === stepId && edge.handlerType === HANDLER_TYPES.FLOW_SET_STEP,
        )
        .forEach((edge) => {
          noHistoryActions.setCustom(edge.actionNodeId, (custom: CustomNodeData) => {
            // eslint-disable-next-line no-param-reassign
            custom.actions = custom.actions?.filter(
              (action) => action.handler.type !== HANDLER_TYPES.FLOW_SET_STEP,
            );
          });
        });
    });

    if (!deletingActiveStep) {
      // if active step is not the deleted step, keep it selected
      flow.onSetStepId(flow.activeStepId);
    } else {
      actions.setNodeEvent(EventTypes.selected, [query.node(nodeId).get().data.nodes[index]]);
      flow.onSetStepId(stepIdToSelect);
    }
  };

  return {
    canDeleteSteps: flow.stepIds.length > 1,
    onDeleteStepId,
  };
};
